import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useLoader } from "../../utils/LoaderUtils";
import { showError, showSuccess } from "../../utils/toastUtils";
import cmsPagesAPI from "../../api/cmsPagesApi";
import slugify from "slugify";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import StoreAPI from "../../api/StoreApi";
import SEOFields from "../../components/partials/SEOFields";
import { sanitizeHTML } from "../../utils/sanitize";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import AiAPI from "../../api/aiApi";
import Auth from "../../utils/AuthUtils";

const CMS_CONTENT = {
  "about-us": {
    title: "About Us",
    description: "Please read about us carefully before using our services.",
  },
  "terms-and-conditions": {
    title: "Terms & Conditions",
    description:
      "Please read our terms and conditions carefully before using our services.",
  },
  "privacy-policy": {
    title: "Privacy Policy",
    description:
      "Understand how we collect, use, and protect your personal information.",
  },
  "return-and-refund-policy": {
    title: "Return & Refund Policy",
    description: "Learn about our return and refund process and conditions.",
  },
  "shipping-and-delivery-policy": {
    title: "Shipping & Delivery Policy",
    description: "Learn about our shipping and delivery policy.",
  },
  "cancellation-policy": {
    title: "Cancellation Policy",
    description: "Learn about our cancellation process and conditions.",
  },
};

function AddNew() {
  const { id } = useParams();
  const location = useLocation();
  const { preslug } = location.state || "";

  const page = useMemo(() => {
    return (
      CMS_CONTENT[id] || {
        title: "CMS Page",
        description: "Content not found.",
      }
    );
  }, [id]);

  const editorRef = useRef();
  const tooltipRef = useRef();
  const { showLoader, hideLoader } = useLoader();
  const user = Auth.getUserDetails();

  const [formData, setFormData] = useState({
    title: page.title || "",
    content: "",
    slug: "",
  });

  const [seoData, setSeoData] = useState({
    metaTitle: "",
    metaDescription: "",
    tags: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      content: content,
    }));
  };

  const [keywords, setKeywords] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preslug) {
      fetchDetails();
    }

    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setShowPrompt(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [preslug]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      title: page.title || "",
      slug: preslug || "",
    }));
  }, [id, preslug]);

  const fetchDetails = async () => {
    setFormData((prev) => ({
      ...prev,
      content: "",
    }));

    setSeoData({
      metaDescription: "",
      metaTitle: "",
      tags: [],
    });

    try {
      const res = await cmsPagesAPI.details(preslug);

      if (res.code === 200) {
        const data = res.data;

        if (data !== null) {
          setFormData({
            title: data?.title || "",
            content: data?.content || "",
            slug: preslug,
          });

          setSeoData({
            metaDescription: data?.meta?.description || "",
            metaTitle: data?.meta?.title || "",
            tags:
              data?.meta?.keywords.split(",").map((tag) => tag.trim()) || [],
          });
        }
      }
    } catch (err) {}
  };

  const parseMarkdown = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/__(.*?)__/g, "<u>$1</u>")
      .replace(/\n/g, "<br>");
  };

  const typeWriterEffect = (markdownText) => {
    let i = 0;
    const plainText = markdownText;
    const finalHtml = parseMarkdown(markdownText);
    const instance = editorRef.current;
    if (!instance) return;
    instance.setContents("");
    const interval = setInterval(() => {
      const partial = plainText
        .substring(0, i)
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
      instance.setContents(partial);
      i++;
      if (i > plainText.length) {
        clearInterval(interval);
        instance.setContents(finalHtml);
      }
    }, 10);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowPrompt(false);
    setLoading(true);

    try {
      const res = await StoreAPI.getGeneralDetails(
        user.storeId || localStorage.getItem("selectedStoreID"),
      );

      if (res.code === 200) {
        const { name, email, address, phone, district, state, pincode } =
          res.data;
        let prompt = `Generate a complete and professional ${page.title} document for the following company.
                      Company Name: ${name}
                      Contact Email: ${email}
                      Contact Phone: ${phone}
                      Address: ${address} ${district} ${state} ${pincode}

                      Requirements:
                      - Do NOT use symbols like #, ##, ###, *, **.
                      - Write in clean paragraph format.
                      - Keep it website-ready for an ecommerce site.
                      `;

        if (keywords) {
          prompt += ` Include and naturally optimize for these keywords: ${keywords}.`;
        }

        prompt += `IMPORTANT:
The output must be pure raw text.
If any markdown symbols like # or * are included, the response will be rejected.
`;

        const result = await AiAPI.getContent({ prompt });
        typeWriterEffect(result.data);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required.";
    if (!formData.content.trim()) newErrors.message = "Content is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    showLoader();

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const cleanContent = sanitizeHTML(editorRef.current.getContents());

      const payload = {
        ...formData,
        content: cleanContent,
        metaTitle: sanitizeHTML(seoData.metaTitle),
        metaDescription: sanitizeHTML(seoData.metaDescription),
        keywords: seoData.tags.join(","),
      };

      const res = await cmsPagesAPI.store(payload);

      res.code === 200 ? showSuccess(res.message) : showError(res.message);
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  const allowedSlugs = Object.keys(CMS_CONTENT);
  if (!allowedSlugs.includes(id)) {
    return <Navigate to="/404" replace />;
  }

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>{page.title}</h4>
            <h6>{page.description}</h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Add Details</h4>
          </div>
          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-12 col-sm-12 mb-3">
                  <label className="form-label">
                    Title<span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text" id="basic-addon1">
                      <i className="fa fa-pencil"></i>
                    </span>
                    <input
                      type="text"
                      name="title"
                      className="form-control"
                      placeholder="Title"
                      value={formData.title}
                      onChange={handleChange}
                      readOnly
                    />
                  </div>
                  {errors.title && (
                    <span className="errors text-danger">{errors.title}</span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Description<span className="text-danger">*</span>
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        type="button"
                        className="ms-3 btn btn-sm btn-secondary"
                        onClick={() => setShowPrompt(true)}
                        disabled={loading}
                      >
                        {loading
                          ? "Generating..."
                          : `Generate ${page.title} using AI`}
                      </button>

                      {showPrompt && (
                        <div ref={tooltipRef} className="tooltipRef">
                          <div>
                            <label style={{ fontWeight: "bold" }}>
                              Optional Keywords
                            </label>
                            <input
                              type="text"
                              className="form-control my-2"
                              placeholder="Enter comma-separated keywords"
                              value={keywords}
                              onChange={(e) => setKeywords(e.target.value)}
                            />
                            <div className="d-flex justify-content-end">
                              <button
                                className="btn btn-sm btn-light me-2"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowPrompt(false);
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={handleGenerate}
                                disabled={loading}
                              >
                                {loading ? "Generating..." : "Generate"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                  <SunEditor
                    getSunEditorInstance={(sunEditor) => {
                      editorRef.current = sunEditor;
                    }}
                    height="250px"
                    setOptions={{
                      buttonList: [
                        ["undo", "redo"],
                        ["font", "fontSize"],
                        ["formatBlock"],
                        ["bold", "underline", "italic", "strike"],
                        ["fontColor", "hiliteColor"],
                        ["align", "list", "table"],
                        ["link", "image", "video"],
                        ["fullScreen", "preview", "print"],
                        ["codeView"],
                      ],
                      allowedClassNames: ".*", // sab classes allow
                      attributesWhitelist: {
                        all: "style|class|id",
                      },
                      addTagsWhitelist: ".*", // extra tags remove na kare
                      imageFileInput: true,
                      font: [
                        "Arial",
                        "Comic Sans MS",
                        "Courier New",
                        "Georgia",
                        "Tahoma",
                        "Trebuchet MS",
                        "Verdana",
                      ],
                      fontSize: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36], // include 12px
                    }}
                    setContents={formData.content}
                    onChange={handleEditorChange}
                  />
                  {errors.message && (
                    <span className="errors text-danger">{errors.message}</span>
                  )}
                </div>
              </div>

              <hr />

              <SEOFields
                seoData={seoData}
                setSeoData={setSeoData}
                slug={slugify(formData.title || "", { lower: true })}
                errors={errors}
              />

              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-end">
                  {!isLoading && (
                    <button
                      type="submit"
                      id="submitBtn"
                      className="btn btn-primary shadow-none"
                    >
                      Submit Details
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddNew;
